import { useCallback, useEffect, useState } from "react"

import { fetchJsonCached, getCachedJson } from "@/shared/lib/cached-json"

import type { CompatibilityData, CompatibilityPair, ZodiacSign } from "../types"

export const COMPATIBILITY_DATA_URL = "/json/compatibility.json"

/**
 * Loads the static compatibility dataset (all zodiac pairs) once and exposes a
 * pair lookup. The 980 KB file lives in `public/json/` and is fetched lazily so
 * it never bloats the main bundle; the fetch is cached for the session.
 */
export function useCompatibilityData() {
  const [data, setData] = useState<CompatibilityData | null>(
    () => getCachedJson<CompatibilityData>(COMPATIBILITY_DATA_URL) ?? null,
  )
  const [loading, setLoading] = useState(() => !getCachedJson(COMPATIBILITY_DATA_URL))

  useEffect(() => {
    if (data) return
    let cancelled = false
    fetchJsonCached<CompatibilityData>(COMPATIBILITY_DATA_URL)
      .then((loaded) => {
        if (!cancelled) {
          setData(loaded)
          setLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [data])

  const getPair = useCallback(
    (first: ZodiacSign, second: ZodiacSign): CompatibilityPair | null =>
      data?.[first]?.[second] ?? null,
    [data],
  )

  return { loading, getPair }
}
